// Group batch attendance manager.
import { useState, useEffect, useRef } from "react";
import { doc, setDoc, deleteDoc, getDocs, writeBatch } from "firebase/firestore";
import { db, userCol } from "../lib/firebase";
import { clientToday, canMarkAttendance } from "../domain";
import { uid, cl } from "../lib/format";
import { markCounterPatch } from "../lib/packages";
import { themeTokens } from "../lib/theme";
import { Btn, Icon, ConfirmButton, useToast } from "../ui/primitives";

export const BatchManager=({clients,setClients,attendanceRecords=[],theme,userId,profile})=>{
  const toast=useToast();
  const [batches,setBatches]=useState([]);
  const [modal,setModal]=useState(null); // null | "create" | "edit" | "attend"
  const [sel,setSel]=useState(null);
  const [form,setForm]=useState({name:"",time:"",days:[],clientIds:[]});
  const [attendDate,setAttendDate]=useState(clientToday());
  const [present,setPresent]=useState([]);
  const markedRef=useRef(new Set()); // "clientId_date" marked this session — guards a re-tap before attendanceRecords re-syncs
  const {isDark,tc,mc,bc}=themeTokens(theme);
  const cb=isDark?"#1e293b":"#fff";

  useEffect(()=>{
    if(!userId)return;
    getDocs(userCol(userId,"batches")).then(s=>setBatches(s.docs.map(d=>({id:d.id,...d.data()}))));
  },[userId]);

  const saveBatch=async(b)=>{
    const {id,...data}=b;
    try{await setDoc(doc(db,"users",userId,"batches",id),data);}
    catch(e){console.error("Batch save failed:",e);toast("Couldn't save batch — check your connection and try again.","error");throw e;}
  };
  const openCreate=()=>{setForm({name:"",time:"",days:[],clientIds:[]});setModal("create");};
  const openEdit=(b)=>{setSel(b);setForm({name:b.name,time:b.time||"",days:b.days||[],clientIds:b.clientIds||[]});setModal("edit");};
  const openAttend=(b)=>{setSel(b);setPresent(b.clientIds||[]);setAttendDate(clientToday());setModal("attend");};
  const saveForm=async()=>{
    if(!form.name.trim()){toast("Enter a batch name.","warning");return;}
    const isCreate=modal==="create";
    if(isCreate&&!form.days?.length){toast("Select at least one day.","warning");return;}
    if(isCreate){
      const b={id:uid(),...form};
      setBatches(prev=>[...prev,b]);await saveBatch(b);
      toast("Batch created.","success");
    } else {
      const updated={...sel,...form};
      setBatches(prev=>prev.map(b=>b.id===sel.id?updated:b));await saveBatch(updated);
      toast("Batch updated.","success");
    }
    setModal(null);
  };
  const deleteBatch=async(b)=>{
    setBatches(prev=>prev.filter(x=>x.id!==b.id));
    try{
      await deleteDoc(doc(db,"users",userId,"batches",b.id));
      toast(`Batch "${b.name}" deleted`,"info");
    }catch(e){console.error("Batch delete failed:",e);toast("Couldn't delete batch — check your connection and try again.","error");}
  };
  const markAttendance=()=>{
    const okRows=[];const skipped=[];
    for(const cid of present){
      const c=clients.find(x=>x.id===cid);if(!c)continue;
      if(markedRef.current.has(`${cid}_${attendDate}`)){skipped.push(c.name);continue;}
      const g=canMarkAttendance(c,attendDate,{existingForClient:attendanceRecords,time:sel.time||"",now:new Date()});
      if(!g.ok){skipped.push(c.name);continue;}
      okRows.push({id:uid(),clientId:cid,clientName:c.name,date:attendDate,time:sel.time||"",note:`Batch: ${sel.name}`,batchId:sel.id});
    }
    if(okRows.length===0){toast(`Nothing to mark${skipped.length?` — ${skipped.length} skipped`:""}.`,"warning");return;}
    const okIds=new Set(okRows.map(r=>r.clientId));
    okRows.forEach(r=>markedRef.current.add(`${r.clientId}_${r.date}`));
    // optimistic counter update first (batch component has no local records list)
    if(setClients)setClients(prev=>prev.map(x=>okIds.has(x.id)?{...x,...markCounterPatch(x)}:x));
    toast(`${okRows.length} marked${skipped.length?` · ${skipped.length} skipped`:""} in "${sel.name}"`,skipped.length?"warning":"success");
    setModal(null);
    if(userId){
      const b=writeBatch(db);
      okRows.forEach(r=>{const{id,...d}=r;b.set(doc(db,"users",userId,"attendance",id),d);});
      b.commit().catch(e=>{console.error("Batch attendance sync failed:",e);toast("Some attendance didn't sync — it'll retry when you're online.","warning");});
    }
  };
  const DAYS=["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const toggleDay=(d)=>setForm(f=>({...f,days:f.days.includes(d)?f.days.filter(x=>x!==d):[...f.days,d]}));
  const toggleClient=(id)=>setForm(f=>({...f,clientIds:f.clientIds.includes(id)?f.clientIds.filter(x=>x!==id):[...f.clientIds,id]}));
  const togglePresent=(id)=>setPresent(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);

  return(
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold" style={{color:tc}}>Batches / Groups</h2>
        <Btn onClick={openCreate} theme={theme}><Icon name="plus" size={16}/>New Batch</Btn>
      </div>
      {batches.length===0&&<p className="text-sm text-center py-8" style={{color:mc}}>No batches yet. Create one to manage group classes.</p>}
      {batches.map(b=>(
        <div key={b.id} className="rounded-2xl p-4 border" style={{background:cb,borderColor:bc}}>
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold text-sm" style={{color:tc}}>{b.name}</p>
              <p className="text-xs mt-0.5" style={{color:mc}}>{b.time&&`${b.time} · `}{(b.days||[]).join(", ")||"No days set"} · {(b.clientIds||[]).length} clients</p>
            </div>
            <div className="flex gap-2">
              <button onClick={()=>openAttend(b)} className="px-3 py-1.5 rounded-xl text-xs font-semibold" style={{background:theme?.light||"#ccfbf1",color:theme?.primary}}>Mark Attendance</button>
              <button onClick={()=>openEdit(b)} className="p-1.5 rounded-lg" style={{color:mc}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </button>
              <ConfirmButton
                label={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>}
                confirmLabel="Delete?"
                onConfirm={()=>deleteBatch(b)}
                danger
                className="p-1.5 rounded-lg text-red-400"
              />
            </div>
          </div>
        </div>
      ))}

      {/* Create/Edit Modal */}
      {(modal==="create"||modal==="edit")&&(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-4" onClick={()=>setModal(null)}>
          <div className="bg-white rounded-3xl w-full max-w-md max-h-[85vh] flex flex-col" onClick={e=>e.stopPropagation()}>
            <div className="p-6 pb-0 overflow-y-auto">
            <h3 className="font-bold text-gray-800 mb-4">{modal==="create"?"New Batch":"Edit Batch"}</h3>
            <div className="space-y-4 pb-6">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{color:"#6b7280"}}>Batch Name</label>
                <input className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal-500"
                  placeholder="e.g. Morning Yoga 7am" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{color:"#6b7280"}}>Class Time</label>
                <input type="time" className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal-500"
                  value={form.time} onChange={e=>setForm(f=>({...f,time:e.target.value}))}/>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{color:"#6b7280"}}>Days</label>
                <div className="flex flex-wrap gap-2">
                  {DAYS.map(d=>(
                    <button key={d} onClick={()=>toggleDay(d)} className="px-3 py-1.5 rounded-xl text-xs font-semibold border-2 transition-all"
                      style={{background:form.days.includes(d)?"#ccfbf1":"transparent",borderColor:form.days.includes(d)?"#0f766e":"#e5e7eb",color:form.days.includes(d)?"#0f766e":"#6b7280"}}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>{`Add ${cl(profile,2)} to Batch`}</label>
                <div className="max-h-48 overflow-y-auto space-y-1 border rounded-xl p-2" style={{borderColor:bc}}>
                  {clients.length===0&&<p className="text-xs text-center py-2" style={{color:mc}}>{`No ${cl(profile,2).toLowerCase()} yet`}</p>}
                  {clients.map(c=>(
                    <button key={c.id} onClick={()=>toggleClient(c.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all"
                      style={{background:form.clientIds.includes(c.id)?"#f0fdf4":"transparent"}}>
                      <div className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all"
                        style={{background:form.clientIds.includes(c.id)?"#16a34a":"transparent",borderColor:form.clientIds.includes(c.id)?"#16a34a":"#d1d5db"}}>
                        {form.clientIds.includes(c.id)&&<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                      </div>
                      <span className={form.clientIds.includes(c.id)?"font-semibold text-green-700":"text-gray-700"}>{c.name}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs" style={{color:mc}}>{form.clientIds.length} client(s) selected</p>
              </div>
            </div>
            </div>
            <div className="flex gap-3 p-6 pt-4 border-t border-gray-100 flex-shrink-0">
              <button onClick={()=>setModal(null)} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={saveForm} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white" style={{background:theme?.primary||"#0f766e"}}>Save Batch</button>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Modal */}
      {modal==="attend"&&sel&&(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-4" onClick={()=>setModal(null)}>
          <div className="rounded-3xl p-6 w-full max-w-md" style={{background:cb}} onClick={e=>e.stopPropagation()}>
            <p className="font-semibold text-base mb-1" style={{color:tc}}>Mark Attendance</p>
            <p className="text-sm mb-4" style={{color:mc}}>{sel.name} · {sel.time}</p>
            <div className="flex flex-col gap-1 mb-4">
              <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>Date</label>
              <input type="date" className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none"
                style={{borderColor:bc,background:isDark?"#0f172a":"#f9fafb",color:tc}}
                value={attendDate} onChange={e=>setAttendDate(e.target.value)}/>
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{color:mc}}>Deselect Absentees</p>
            <div className="space-y-1 max-h-56 overflow-y-auto mb-4">
              {(sel.clientIds||[]).map(cid=>{
                const c=clients.find(x=>x.id===cid);
                if(!c)return null;
                const isPresent=present.includes(cid);
                return(
                  <button key={cid} onClick={()=>togglePresent(cid)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all border-2"
                    style={{background:isPresent?"#f0fdf4":"#fef2f2",borderColor:isPresent?"#bbf7d0":"#fecaca"}}>
                    <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{background:isPresent?"#16a34a":"#ef4444"}}>
                      {isPresent
                        ?<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                        :<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      }
                    </div>
                    <span className="text-sm font-medium" style={{color:isPresent?"#15803d":"#b91c1c"}}>{c.name}</span>
                    <span className="ml-auto text-xs" style={{color:isPresent?"#16a34a":"#ef4444"}}>{isPresent?"Present":"Absent"}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs mb-4" style={{color:mc}}>{present.length} present · {(sel.clientIds||[]).length-present.length} absent</p>
            <div className="flex gap-3">
              <button onClick={()=>setModal(null)} className="flex-1 py-2.5 rounded-xl border-2 text-sm font-semibold" style={{borderColor:bc,color:mc}}>Cancel</button>
              <button onClick={markAttendance} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white" style={{background:theme?.primary||"#0f766e"}}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
